import type { Questionnaire, QuestionnaireItem, QuestionnaireResponse } from '../types';
import { dayDiff, daysAgoAt, parseDate, startOfToday } from '../lib/dates';
import { babies, facilities, flags } from './mockData';
import { checkinPlans, checkinResponses, questionBank } from './clinicalData';
import { ANSWER_CODE } from './codes';
import { addAudit, type Actor } from './auditService';
import { respond } from './respond';

export const UNCLEAR = 'unknown';
const ANSWER_HI: Record<string, string> = { yes: 'हाँ', no: 'नहीं', [UNCLEAR]: 'साफ़ नहीं' };
const CALLBACK_AFTER = 3;

export function isRedFlag(q: QuestionnaireItem, value: string, ageDays?: number): boolean {
  if (!q.redFlag || value === '' || value === UNCLEAR) return false;
  const { op, value: target, underDays } = q.redFlag;
  if (underDays !== undefined && (ageDays === undefined || ageDays >= underDays)) return false;
  if (op === 'eq') return value.trim().toLowerCase() === target.trim().toLowerCase();
  const n = Number(value);
  return !Number.isNaN(n) && (op === 'gt' ? n > Number(target) : n < Number(target));
}

export async function getQuestions(facilityId: string): Promise<QuestionnaireItem[]> {
  return respond(questionBank.filter((q) => q.facilityId === facilityId).map((q) => ({ ...q })));
}

// Any edit sends the question back to Draft until a doctor approves it again.
export async function saveQuestion(input: QuestionnaireItem, actor?: Actor): Promise<void> {
  const q = { ...input, id: input.id || `${input.facilityId}-q-${Date.now()}`, status: 'draft' as const };
  q.code = ANSWER_CODE(q.id, q.textEn);
  const i = questionBank.findIndex((x) => x.id === q.id);
  if (i >= 0) questionBank[i] = q;
  else questionBank.push(q);
  addAudit(actor, 'question.saved', q.textEn, q.facilityId);
  return respond(undefined);
}

export async function approveQuestion(id: string, actor?: Actor): Promise<void> {
  if (actor?.role !== 'doctor') throw new Error('Only doctors can approve questions');
  const q = questionBank.find((x) => x.id === id);
  if (q) q.status = 'approved';
  addAudit(actor, 'question.approved', q?.textEn ?? id, q?.facilityId);
  return respond(undefined);
}

export async function getPlans(facilityId: string): Promise<Questionnaire[]> {
  return respond(checkinPlans.filter((p) => p.facilityId === facilityId).map((p) => JSON.parse(JSON.stringify(p))));
}

export async function savePlan(plan: Questionnaire, actor?: Actor): Promise<void> {
  const approved = plan.itemIds.every((id) => questionBank.find((q) => q.id === id)?.status === 'approved');
  if (!approved || !plan.itemIds.length) throw new Error('Plans may only use approved questions');
  const p = { ...plan, id: plan.id || `plan-${Date.now()}` };
  const i = checkinPlans.findIndex((x) => x.id === p.id);
  if (i >= 0) checkinPlans[i] = p;
  else checkinPlans.push(p);
  addAudit(actor, 'plan.saved', p.name, p.facilityId);
  return respond(undefined);
}

export async function getCheckinResponses(motherId: string): Promise<QuestionnaireResponse[]> {
  return respond(checkinResponses.filter((r) => r.subjectId === motherId).sort((a, b) => b.authored.localeCompare(a.authored)));
}

/** "Baby has fever (the mother said "yes" on the check-in call)". Falls back to the question when no plain words are set. */
export function plainFlag(q: { textEn: string; textHi: string; flagEn?: string; flagHi?: string }, value: string, lang: 'en' | 'hi'): string {
  if (lang === 'hi') return `${q.flagHi ?? q.textHi ?? q.textEn} (चेक-इन कॉल पर माँ ने "${ANSWER_HI[value] ?? value}" कहा)`;
  return `${q.flagEn ?? q.textEn} (the mother said "${value}" on the check-in call)`;
}

// Answers the mother gave on a call. Red-flag answers raise an urgent flag through the normal flag flow.
function record(facilityId: string, motherId: string, planId: string, answers: { itemId: string; value: string }[], authored: string, actor?: Actor): QuestionnaireResponse {
  const f = facilities.find((x) => x.id === facilityId);
  const doctor = f?.doctors[0] ? `${f.doctors[0].title} ${f.doctors[0].name}` : '';
  const dob = babies.find((b) => b.motherId === motherId)?.dob;
  const ageDays = dob ? dayDiff(parseDate(dob), startOfToday()) : undefined;
  const res: QuestionnaireResponse = {
    resourceType: 'QuestionnaireResponse', id: `qr-${motherId}-${checkinResponses.length + 1}`, facilityId, subjectId: motherId, questionnaireId: planId, authored, source: 'call',
    answers: answers.map((a) => {
      const q = questionBank.find((x) => x.id === a.itemId)!;
      const red = isRedFlag(q, a.value, ageDays);
      const answer = { itemId: q.id, value: a.value, code: q.code, redFlag: red, flagId: undefined as string | undefined };
      if (red) {
        answer.flagId = `f-ci-${Date.now()}-${flags.length}`;
        flags.push({ id: answer.flagId, facilityId, motherId, reason: plainFlag(q, a.value, 'en'), reasonHi: plainFlag(q, a.value, 'hi'), checkin: { questionEn: q.flagEn ?? q.textEn, questionHi: q.flagHi ?? q.textHi ?? q.textEn, answerEn: a.value.charAt(0).toUpperCase() + a.value.slice(1), answerHi: ANSWER_HI[a.value] ?? a.value }, urgency: 'urgent', status: 'open', assignedTo: doctor, raisedAt: authored });
      }
      return answer;
    }),
  };
  const unclear = res.answers.filter((a) => a.value === UNCLEAR).length;
  if (unclear >= CALLBACK_AFTER) {
    res.callbackFlagId = `f-cb-${Date.now()}-${flags.length}`;
    flags.push({ id: res.callbackFlagId, facilityId, motherId, reason: `Staff call-back needed: ${unclear} unclear check-in answers`, reasonHi: `स्टाफ़ वापस कॉल करे: चेक-इन के ${unclear} जवाब साफ़ नहीं थे`, urgency: 'normal', status: 'open', assignedTo: 'Staff', raisedAt: authored });
  }
  checkinResponses.push(res);
  addAudit(actor, 'checkin.recorded', `Patient ${motherId}`, facilityId, res.answers.some((a) => a.redFlag) ? 'red flag' : undefined, authored);
  return res;
}

export async function recordCheckin(facilityId: string, motherId: string, planId: string, answers: { itemId: string; value: string }[], actor?: Actor): Promise<QuestionnaireResponse> {
  return respond(record(facilityId, motherId, planId, answers, new Date().toISOString(), actor));
}

const voice: Actor = { id: 'agent-baby', name: 'Call agent', role: 'staff' };
const S = 'sharma-meerut';
record(S, 'm-s1', `plan-${S}-postvax`, [{ itemId: `${S}-q-fever`, value: 'yes' }, { itemId: `${S}-q-fever-temp`, value: '100.8 °F' }, { itemId: `${S}-q-swelling`, value: 'no' }, { itemId: `${S}-q-feeding`, value: 'yes' }], daysAgoAt(0, 10, 5), voice);
record(S, 'm-s3', `plan-${S}-postvax`, [{ itemId: `${S}-q-fever`, value: 'no' }, { itemId: `${S}-q-swelling`, value: 'yes' }, { itemId: `${S}-q-feeding`, value: UNCLEAR }], daysAgoAt(0, 9, 40), voice);
